import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import * as Yup from "yup";
import DropzoneComponent from "../Dropzone.js";
import { domainConfig } from "../../config.js";
import {
  activityAvailabilityValidationSchema,
  createActivityAvailability,
} from "./ActivityAvailability.js";

import ActivityScoreSelector from "./ActivityScoreSelector.js";
export const PHOTO_SUBMISSION_ACTIVITY_ID = 2;
export const PHOTO_COMPETITION_ACTIVITY_ID = 3;
const PHOTO_ACTIVITY_DATE_FIELDS = ["submission_start_at", "submission_end_at", "voting_start_at", "voting_end_at"];

export const formatPhotoActivityJsonForForm = (json = {}) => {
  const normalized = { ...json };
  PHOTO_ACTIVITY_DATE_FIELDS.forEach((field) => delete normalized[field]);
  delete normalized.score_boost;
  return normalized;
};

export const normalizePhotoActivityJsonForSubmit = (json = {}) => {
  const normalized = { ...json };
  PHOTO_ACTIVITY_DATE_FIELDS.forEach((field) => delete normalized[field]);
  delete normalized.score_boost;
  return normalized;
};

export const createPhotoActivityJson = (appId) => ({
  availability: createActivityAvailability(),
  theme: "",
  description: "",
  cover_image: "",
  rejection_reason_required: true,
  auto_publish: true,
  ...(Number(appId) === PHOTO_COMPETITION_ACTIVITY_ID
    ? {
      allow_self_like: false,
      single_vote_per_participant: true,
    }
    : {}),
});

export const photoActivityValidationSchema = (appId, dataTable = {}) => Yup.object().shape({
  title: Yup.string().required("Campo obrigatório").max(100, "Máximo de 100 caracteres"),
  app_id: Yup.number().oneOf([PHOTO_SUBMISSION_ACTIVITY_ID, PHOTO_COMPETITION_ACTIVITY_ID]).required(),
  json: Yup.object().shape({
    availability: activityAvailabilityValidationSchema(dataTable),
    theme: Yup.string().required("Campo obrigatório").max(200, "Máximo de 200 caracteres"),
    cover_image: Yup.string().trim().required("Campo obrigatório"),
    description: Yup.string().max(2000, "Máximo de 2000 caracteres"),
    auto_publish: Yup.boolean().required("Campo obrigatório"),
    allow_self_like: Yup.boolean().notRequired(),
    single_vote_per_participant: Yup.boolean().notRequired(),
    score_id: Number(appId) === PHOTO_SUBMISSION_ACTIVITY_ID
      ? Yup.number().positive("Campo obrigatório").nullable()
      : Yup.number().nullable(),
    winner_score_id: Number(appId) === PHOTO_COMPETITION_ACTIVITY_ID
      ? Yup.number().positive("Campo obrigatório").nullable()
      : Yup.number().nullable(),
  }),
});


const ActivityPhotoForm = ({
  appId,
  values,
  setFieldValue,
  setFieldTouched,
  errors = {},
  touched = {},
  availableScores = [],
  eventId,
  handlePreviewSuccess,
  handlePreviewError,
  availabilityFields,
  onScoresChanged,
}) => {
  const isCompetition = Number(appId) === PHOTO_COMPETITION_ACTIVITY_ID;

  return (
    <>
      <Row>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Título</Form.Label>
          <Form.Control
            value={values.title || ""}
            onChange={(event) => setFieldValue("title", event.target.value)}
            isInvalid={Boolean(touched.title && errors.title)}
          />
          <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
        </Form.Group>
        {availabilityFields}
        <Form.Group as={Col} md={12} className="my-3">
          <Form.Label>Tema</Form.Label>
          <Form.Control
            value={values.json?.theme || ""}
            onChange={(event) => setFieldValue("json.theme", event.target.value)}
            isInvalid={Boolean(touched.json?.theme && errors.json?.theme)}
          />
          <Form.Control.Feedback type="invalid">{errors.json?.theme}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Imagem de referência</Form.Label>
          <div className="imagesUpload">
            <img
              className={`h-100 img-thumbnail ${touched.json?.cover_image && errors.json?.cover_image ? "is-invalid" : ""}`}
              src={values.json?.cover_image
                ? `${domainConfig.imageServer}/plataforma-eventos/${eventId}/${values.json.cover_image}`
                : `${domainConfig.imageServer}/empty-background.png`}
              alt="Prévia da imagem de referência"
            />
            <Form.Control.Feedback type="invalid">
              {errors.json?.cover_image}
            </Form.Control.Feedback>
          </div>
          <DropzoneComponent
            className="my-3 dropzoneComp"
            type="image"
            handlePreviewSuccess={handlePreviewSuccess}
            handlePreviewError={handlePreviewError}
            setFieldValue={setFieldValue}
            folder="plataforma-eventos"
            id={eventId}
            field="json.cover_image"
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="mb-3">
          <Form.Label>Instruções</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={values.json?.description || ""}
            onChange={(event) => setFieldValue("json.description", event.target.value)}
            isInvalid={Boolean(touched.json?.description && errors.json?.description)}
          />
          <Form.Control.Feedback type="invalid">{errors.json?.description}</Form.Control.Feedback>
        </Form.Group>
        <ActivityScoreSelector
          type={isCompetition ? "activity_photo_competition_winner" : "activity_photo_approval"}
          name={isCompetition ? "json.winner_score_id" : "json.score_id"}
          label="Pontuação"
          value={isCompetition ? values.json?.winner_score_id : values.json?.score_id}
          scores={availableScores}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          error={isCompetition ? errors.json?.winner_score_id : errors.json?.score_id}
          touched={isCompetition ? touched.json?.winner_score_id : touched.json?.score_id}
          eventId={eventId}
          onScoresChanged={onScoresChanged}
        />
        <Form.Group as={Col} md={12} className="d-flex align-items-end">
          <Form.Check
            type="checkbox"
            label="Exigir motivo ao reprovar"
            checked={values.json?.rejection_reason_required !== false}
            onChange={(event) => setFieldValue("json.rejection_reason_required", event.target.checked)}
          />
        </Form.Group>
        <Form.Group as={Col} md={12} className="d-flex align-items-end">
          <Form.Check
            type="checkbox"
            label="Publicar foto aprovada no Newsfeed"
            checked={values.json?.auto_publish !== false}
            onChange={(event) => setFieldValue("json.auto_publish", event.target.checked)}
          />
        </Form.Group>
        {isCompetition && (
          <>
            <Form.Group as={Col} md={12}>
              <Form.Check
                type="checkbox"
                label="Permitir curtida na própria foto"
                checked={values.json?.allow_self_like === true}
                onChange={(event) => setFieldValue("json.allow_self_like", event.target.checked)}
              />
            </Form.Group>
            <Form.Group as={Col} md={12}>
              <Form.Check
                type="checkbox"
                label="Permitir apenas uma curtida por participante"
                checked={values.json?.single_vote_per_participant === true}
                onChange={(event) => setFieldValue("json.single_vote_per_participant", event.target.checked)}
              />
            </Form.Group>
          </>
        )}
      </Row>
    </>
  );
};

export default ActivityPhotoForm;
